import {
	EditOutlined,
	HolderOutlined,
	PlusOutlined,
	SignatureOutlined,
} from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Form } from "components/Form/Form"
import { InputRTE } from "components/Input/InputRTE"
import { Modal } from "components/Modal/Modal"
import { TagSelect } from "components/TagSelect/TagSelect"
import { useDisclosure } from "hooks/useDisclosure"
import { TimelineNote, TimelineNotePayload } from "interface/patientType"
import { groupBy, pick } from "lodash-es"
import { useCallback, useMemo } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { useConfigClinicalNoteLabels } from "services/globalConfig"
import {
	useCreateTimelineNote,
	useDeleteTimelineNote,
	useUpdateTimelineNote,
} from "services/patients"
import { cn } from "styles/cn"
import { humanReadable } from "utils"
import { asDate, dateFormatter } from "utils/date"
import { Footnote } from "./Footnote"

export const PatientTimelineNote = Object.assign(Edit, { Create })

function Create({
	patientId,
	displayDate,
	enabled = true,
	onToggle,
}: Pick<TimelineNote, "patientId" | "displayDate"> & {
	enabled?: boolean
	onToggle: VoidFunction
}) {
	const add = useDisclosure({ onOpen: onToggle, onClose: onToggle })
	const ariaLabel = "Create timeline note"

	return (
		<div
			role="note"
			aria-label={ariaLabel}
			className={cn(
				"group flex items-center",
				!add.isOpen && "h-11 px-12",
				enabled && "hover:cursor-pointer",
			)}
		>
			{!add.isOpen && (
				<Button
					size="small"
					icon={<PlusOutlined aria-hidden />}
					className={cn("invisible", enabled && "group-hover:visible")}
					onClick={add.open}
				>
					Add clinical note
				</Button>
			)}

			{add.isOpen && (
				<Edit
					mode="create"
					note={{ patientId, displayDate }}
					onToggle={add.close}
				/>
			)}
		</div>
	)
}

function Edit({
	mode,
	note,
	enabled = true,
	className,
	onOpen,
	onClose,
	onCancel,
	onToggle,
	dragHandleProps,
	dragHandleRef,
}: {
	mode: "create" | "update"
	note: Partial<TimelineNote>
	enabled?: boolean
	className?: string
	onOpen?: VoidFunction
	onClose?: VoidFunction
	onCancel?: VoidFunction
	onToggle?: VoidFunction
	dragHandleProps?: Record<string, any>
	dragHandleRef?: (element: HTMLElement | null) => void
}) {
	const isCreate = mode === "create"
	const isUpdate = mode === "update"
	const [form] = Form.useForm<TimelineNotePayload>()
	const createNote = useCreateTimelineNote()
	const updateNote = useUpdateTimelineNote()
	const editing = useDisclosure({
		defaultIsOpen: isCreate,
		onOpen() {
			onOpen?.()
			onToggle?.()
		},
		onClose() {
			onClose?.()
			onToggle?.()
		},
	})
	const submitButtonLabel = isCreate ? "Add note" : "Save"
	const formAriaLabel = `${isCreate ? "Add" : "Update"} timeline note`
	const isPending = createNote.isPending || updateNote.isPending
	const isError = createNote.isError || updateNote.isError
	const errorMessage = createNote.error?.message || updateNote.error?.message
	const { data: { timelineNoteLabels = [] } = {} } =
		useConfigClinicalNoteLabels()
	const options = useMemo(
		() =>
			Object.entries(groupBy(timelineNoteLabels, (label) => label.group || ""))
				.map(([group, items]) => {
					const opts = items.map((item) => ({
						label: item.displayName,
						value: item.key,
						color: item.color ?? "default",
					}))
					return group ? { label: humanReadable(group), options: opts } : opts
				})
				.flat(),
		[timelineNoteLabels],
	)
	const handleCancel = useCallback(() => {
		form.resetFields()
		editing.close()
		onCancel?.()
	}, [editing, form, onCancel])
	const handleEdit = useCallback(
		(values: TimelineNotePayload) => {
			if (isUpdate && note.id && note.displayDate) {
				updateNote.mutate(
					{
						...values,
						id: note.id,
						displayDate: note.displayDate,
					},
					{
						onSuccess() {
							editing.close()
						},
					},
				)
			} else if (isCreate && note.patientId && note.displayDate) {
				createNote.mutate(
					{
						...values,
						patientId: note.patientId,
						displayDate: note.displayDate,
					},
					{
						onSuccess() {
							form.resetFields()
							editing.close()
						},
					},
				)
			}
		},
		[
			createNote,
			editing,
			form,
			isCreate,
			isUpdate,
			note.displayDate,
			note.id,
			note.patientId,
			updateNote,
		],
	)

	useHotkeys("esc", handleCancel, {
		enabled: editing.isOpen && !isPending,
	})
	useHotkeys(
		"mod+enter",
		(e) => {
			e.preventDefault()
			form.submit()
		},
		{
			enabled: Boolean(editing.isOpen && !isPending),
			enableOnFormTags: true,
			enableOnContentEditable: true,
			preventDefault: true,
		},
	)

	return (
		<fieldset
			disabled={isPending}
			className={cn(
				"group relative flex w-full flex-col gap-4",
				"rounded-[20px] border border-mint-20 bg-white p-5 shadow-secondary focus-within:border-green-60 hover:border-green-60",
				isCreate && "my-11",
				className,
			)}
		>
			{editing.isOpen && !isCreate && (
				<div
					ref={dragHandleRef}
					{...dragHandleProps}
					aria-label="Drag to reorder"
					className="focus-outline absolute top-0 left-0 grid h-full w-5 cursor-grab touch-none place-content-center select-none active:cursor-grabbing"
				>
					<HolderOutlined aria-hidden className="text-navy-60" />
				</div>
			)}

			<div className="flex gap-4">
				<Button
					size="large"
					disabled={!isUpdate || !enabled || editing.isOpen}
					aria-label="Edit timeline note"
					onClick={editing.open}
					className="size-11 not-disabled:bg-grey-10 not-disabled:group-focus-within:bg-white not-disabled:group-hover:bg-white"
					icon={
						isUpdate && enabled && !editing.isOpen ? (
							<>
								<SignatureOutlined
									aria-hidden
									className="text-2xl text-navy-60 group-focus-within:hidden group-hover:hidden"
								/>
								<EditOutlined
									aria-hidden
									className="hidden group-focus-within:inline-flex group-hover:inline-flex"
								/>
							</>
						) : (
							<SignatureOutlined
								aria-hidden
								className="text-2xl text-navy-60"
							/>
						)
					}
				/>

				{isUpdate && !editing.isOpen && (
					<div className="flex w-full flex-col gap-2">
						<InputRTE
							readOnly
							minRows={1}
							aria-label="Observations"
							variant="borderless"
							value={note.content}
						/>
						<Footnote
							labels={note.labels?.map(humanReadable)}
							editedBy={`${note.lastEditedByName ?? note.createdByName}, ${asDate(note.updatedAt).fromNow()}`}
							editedByTooltip={dateFormatter(note.updatedAt, {
								hasYear: false,
							})}
						/>
					</div>
				)}

				{editing.isOpen && (
					<Form<TimelineNotePayload>
						form={form}
						autoComplete="off"
						initialValues={note}
						aria-label={formAriaLabel}
						className="flex w-full flex-col"
						onFinish={handleEdit}
					>
						<Form.Item<TimelineNotePayload>
							name="content"
							rules={[
								{ required: true, message: "Please enter timeline note" },
							]}
						>
							<InputRTE
								autoFocus
								aria-label="Observations"
								placeholder="Add the timeline note here..."
							/>
						</Form.Item>
						<Form.Item<TimelineNotePayload> name="labels" noStyle>
							<TagSelect
								disabled={isPending}
								buttonProps={{ children: "Add label" }}
								selectProps={{ options, "aria-label": "Select label" }}
							/>
						</Form.Item>
						<ErrorStatus className="mt-3" show={isError && !isPending}>
							{errorMessage}
						</ErrorStatus>
					</Form>
				)}
			</div>

			{editing.isOpen && (
				<div className="flex gap-3">
					{isUpdate && note && (
						<RemoveNote note={note as TimelineNote} onSuccess={editing.close} />
					)}
					<Button onClick={handleCancel} className="ml-auto">
						Cancel
					</Button>
					<Button type="primary" loading={isPending} onClick={form.submit}>
						{submitButtonLabel}
					</Button>
				</div>
			)}
		</fieldset>
	)
}

function RemoveNote({
	note,
	onSuccess,
}: {
	note: TimelineNote
	onSuccess: VoidFunction
}) {
	const confirmation = useDisclosure()
	const remove = useDeleteTimelineNote()
	const handleDelete = useCallback(() => {
		remove.mutate(pick(note, ["id", "patientId"]), {
			onSuccess() {
				onSuccess()
				confirmation.close()
			},
		})
	}, [confirmation, note, onSuccess, remove])

	return (
		<>
			<Button danger onClick={confirmation.open}>
				Remove note
			</Button>

			{confirmation.isOpen && (
				<Modal
					open
					okButtonProps={{ danger: true }}
					confirmLoading={remove.isPending}
					title={`Do you want to delete timeline note?`}
					okText="Delete note"
					cancelText="Keep note"
					onOk={handleDelete}
					onCancel={confirmation.close}
					classNames={{ body: "flex flex-col gap-2" }}
				>
					This will permanently delete the timeline note.
					<ErrorStatus show={remove.isError}>
						{remove.error?.message}
					</ErrorStatus>
				</Modal>
			)}
		</>
	)
}
